'use client';

import { useGSAP } from '@gsap/react';
import { FC, useEffect, useRef } from 'react';

import Image from '@/components/Image';
import AnimatedButton from '@/components/shared/AnimatedButton';
import { Typography } from '@/components/shared/Typography';
import StrapiRichContent from '@/components/StrapiRichContent';
import cn from '@/helpers/cn';
import useDevice from '@/hooks/useDevice';
import { useLazyVideoSource } from '@/hooks/useLazyVideoSource';
import { useViewportVideosPlayback } from '@/hooks/useViewportVideosPlayback';
import { gsap } from '@/lib/gsap';
import { buildMailto } from '@/lib/utils';

import css from './style.module.scss';
import { RecommendSectionDto } from './types';

interface Props {
	data: RecommendSectionDto;
	className?: string;
	isModal?: boolean;
}

const RecommendSection: FC<Props> = ({ data, className = '', isModal = false }) => {
	const device = useDevice();
	const sectionRef = useRef<HTMLDivElement>(null);

	const shouldLoadVideo = useLazyVideoSource(sectionRef);
	useViewportVideosPlayback([sectionRef]);

	const href = buildMailto(data.mailTo);

	useGSAP(
		() => {
			const wrapper = sectionRef.current;
			const titleEl = wrapper?.querySelector(`.${css.title}`);
			const titleLines = titleEl?.querySelectorAll('p');
			const stepsWrap = wrapper?.querySelector(`.${css.steps}`) as HTMLElement | null;
			const steps = stepsWrap?.querySelectorAll(`.${css.step}`);
			const icons = stepsWrap?.querySelectorAll(`.${css.stepIcon}`);

			if (!wrapper || !titleEl || !stepsWrap || !steps?.length) return;

			const mm = gsap.matchMedia();

			mm.add(
				{
					isDesktop: '(min-width: 992px)',
					isMobile: '(max-width: 991px)'
				},
				context => {
					const { isDesktop } = context.conditions as gsap.Conditions;

					const titleTargets = titleLines && titleLines.length ? Array.from(titleLines) : [titleEl];

					gsap.set(titleTargets, {
						opacity: 0,
						y: 40,
						filter: 'blur(10px)'
					});

					gsap.to(titleTargets, {
						opacity: 1,
						y: 0,
						filter: 'blur(0px)',
						duration: 1.1,
						ease: 'power3.out',
						stagger: titleTargets.length > 1 ? 0.15 : 0,
						scrollTrigger: {
							trigger: titleEl,
							start: 'top 80%',
							toggleActions: 'play none none reverse'
						}
					});

					gsap.set(steps, {
						opacity: 0,
						y: 24,
						filter: 'blur(8px)'
					});

					gsap.to(steps, {
						opacity: 1,
						y: 0,
						filter: 'blur(0px)',
						duration: 0.9,
						ease: 'power3.out',
						stagger: 0.18,
						scrollTrigger: {
							trigger: stepsWrap,
							start: 'top 80%',
							toggleActions: 'play none none reverse'
						}
					});

					if (icons?.length) {
						gsap.set(icons, {
							scale: 0.85,
							opacity: 0
						});

						gsap.to(icons, {
							opacity: 1,
							scale: 1,
							duration: 0.6,
							ease: 'back.out(2)',
							stagger: 0.18,
							scrollTrigger: {
								trigger: stepsWrap,
								start: 'top 80%',
								toggleActions: 'play none none reverse'
							}
						});
					}

					if (isDesktop)
						gsap.to(titleEl, {
							yPercent: -40,
							ease: 'none',
							scrollTrigger: {
								trigger: wrapper,
								start: 'top bottom',
								end: 'bottom top',
								scrub: true
							}
						});
				}
			);

			return () => {
				mm.revert();
			};
		},
		{
			scope: sectionRef
		}
	);

	return (
		<section
			ref={sectionRef}
			id={data?.idSection ? data.idSection : undefined}
			className={cn(css.section, className, isModal ? css.modalBlock : '')}
		>
			<video
				loop
				playsInline
				muted
				controls={false}
				className={css.video}
				preload="none"
				poster="/assets/images/partnership/heads-pic.webp"
			>
				{shouldLoadVideo ? (
					device === 'desktop' ? (
						<>
							<source src="/assets/video/webm/heads-pillow-letters.webm" type="video/webm" />
							<source
								src="/assets/video/mov/heads-pillow-letters.mov"
								type='video/quicktime; codecs="hvc1"'
							/>
						</>
					) : (
						<>
							<source src="/assets/video/mp4/feedback.mp4" type="video/mp4" />
							<source
								src="/assets/video/mov/qream-pillow-letters.mov"
								type='video/quicktime; codecs="hvc1"'
							/>
						</>
					)
				) : null}
			</video>

			<div className={css.container}>
				<div className={css.grid}>
					<Typography uppercase className={css.title} variant={isModal ? 'h3' : 'h2'} tag="h2">
						{data.title ? <StrapiRichContent content={data.title} /> : null}
					</Typography>

					<div className={css.steps}>
						{data.steps.map(({ text, isButton }, i) => (
							<div key={i} className={css.step}>
								<div>
									<Image
										src="/assets/icons/partnership/chub-icon.svg"
										alt=""
										width={32}
										height={32}
										className={css.stepIcon}
									/>
								</div>
								{isButton || i === data.steps.length - 1 ? (
									<div className={css.ctaWrapper}>
										<AnimatedButton href={href} className={css.cta} type="link">
											{text}
										</AnimatedButton>
									</div>
								) : (
									<Typography className={css.stepText} variant="body-l" tag="div">
										{text}
									</Typography>
								)}
							</div>
						))}
					</div>
				</div>
			</div>
		</section>
	);
};

export default RecommendSection;
